import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Formik, Field, FieldArray, Form } from "formik";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit, MdAdd } from "react-icons/md";
import {
    ModalHeader,
    ActionSection,
    SideCancelBtn,
    SubmitBtn,
    InputGroup,
    FieldWrappers,
    Label,
    Wrapper,
    ScrollableContent,
} from "../../Styles/InputStyles";
import { getFoodUnitLabel } from "../../../helpers";
import SelectOptionModal from "../../SelectOptionModal";
import { toast } from "react-toastify";

const AddStockTransfer = ({
    handleClose,
    kitchenList = [],
    inventoryStocks = [],
    addTransferMutate,
    addTransferLoading = false
}) => {
    const [height, setHeight] = useState(window.innerHeight);
    const [isKitchenModalOpen, setIsKitchenModalOpen] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [currentItemIndex, setCurrentItemIndex] = useState(0);

    // Filter out items with zero stock
    const availableItems = useMemo(() =>
        inventoryStocks.filter(item => item.currentCount > 0),
        [inventoryStocks]
    );

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const initialValues = {
        assignedToId: "",
        entries: [
            {
                inventoryId: "",
                quantity: 1,
            }
        ]
    };

    // Get kitchen name by id - memoized
    const getKitchenName = useCallback((id) => {
        if (!id) return "Select Kitchen";
        const kitchen = kitchenList.find(k => k.id === id);
        return kitchen ? kitchen.name : "Select Kitchen";
    }, [kitchenList]);

    // Get item name by id - memoized
    const getItemName = useCallback((id) => {
        if (!id) return "Select Item";
        const item = availableItems.find(i => i.id === id);
        if (item) {
            return `${item.itemName} (${item.currentCount} ${getFoodUnitLabel(item.unitType)} available)`;
        }
        return "Select Item";
    }, [availableItems]);

    // Get item details for display - memoized
    const getItemDetails = useCallback((id) => {
        if (!id) return null;
        return availableItems.find(i => i.id === id);
    }, [availableItems]);

    // Get available items for current index (excluding already selected items)
    const getAvailableItemOptions = useCallback((currentIndex, entries) => {
        // Get all selected item IDs from other entries
        const selectedItemIds = entries
            .filter((_, index) => index !== currentIndex)
            .map(entry => entry.inventoryId)
            .filter(id => id); // Remove empty values

        // Filter available items to exclude already selected ones
        const availableItemsForSelection = availableItems.filter(
            item => !selectedItemIds.includes(item.id)
        );

        // If current entry already has a selected item, include it
        const currentItemId = entries[currentIndex]?.inventoryId;
        if (currentItemId) {
            const currentItem = availableItems.find(item => item.id === currentItemId);
            if (currentItem && !availableItemsForSelection.some(item => item.id === currentItemId)) {
                availableItemsForSelection.unshift(currentItem);
            }
        }

        return availableItemsForSelection.map(item => ({
            label: `${item.itemName} (${item.currentCount} ${getFoodUnitLabel(item.unitType)} available)`,
            value: item.id,
        }));
    }, [availableItems]);

    const validate = (values) => {
        const errors = {};

        if (!values.assignedToId) {
            errors.assignedToId = "Please select a kitchen";
        }

        if (!values.entries || values.entries.length === 0) {
            errors.entries = "Please add at least one item";
            return errors;
        }

        // Check for duplicate items
        const itemIds = values.entries
            .filter(entry => entry.inventoryId)
            .map(entry => entry.inventoryId);

        const duplicateItems = itemIds.filter((id, index) => itemIds.indexOf(id) !== index);
        if (duplicateItems.length > 0) {
            const duplicateError = "Duplicate items found. Please remove duplicates.";
            // Add error to first duplicate entry
            const firstDuplicateIndex = itemIds.findIndex(id => id === duplicateItems[0]);
            if (firstDuplicateIndex !== -1) {
                const entriesErrors = [];
                entriesErrors[firstDuplicateIndex] = { inventoryId: duplicateError };
                errors.entries = entriesErrors;
                return errors;
            }
        }

        const entriesErrors = [];
        let hasEntryError = false;

        values.entries.forEach((entry, index) => {
            const entryError = {};

            if (!entry.inventoryId) {
                entryError.inventoryId = "Please select an item";
                hasEntryError = true;
            }

            if (!entry.quantity || Number(entry.quantity) < 0.01) {
                entryError.quantity = "Quantity must be greater than 0.01";
                hasEntryError = true;
            }

            if (entry.inventoryId) {
                const item = getItemDetails(entry.inventoryId);

                if (
                    item &&
                    Number(entry.quantity) > Number(item.currentCount)
                ) {
                    entryError.quantity = `Only ${item.currentCount} ${getFoodUnitLabel(item.unitType)} available`;
                    hasEntryError = true;
                }
            }

            if (Object.keys(entryError).length > 0) {
                entriesErrors[index] = entryError;
            }
        });

        if (hasEntryError) {
            errors.entries = entriesErrors;
        }

        return errors;
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        if (!values.assignedToId || !values.entries || values.entries.length === 0) {
            toast.error("Please select a kitchen and at least one item");
            return;
        }

        // Filter out items with quantity 0
        const validEntries = values.entries.filter(entry => entry.inventoryId && entry.quantity > 0);
        if (validEntries.length === 0) {
            toast.error("Please add at least one valid item with quantity");
            return;
        }

        const payload = {
            assignedTo: parseInt(values.assignedToId),
            items: validEntries.map(entry => ({
                inventoryId: parseInt(entry.inventoryId),
                quantity: parseFloat(entry.quantity),
            }))
        };

        // Call the mutation
        addTransferMutate(payload);
    };

    const handleKitchenSelect = (selectedValue, setFieldValue) => {
        if (selectedValue) {
            setFieldValue("assignedToId", selectedValue);
        }
        setIsKitchenModalOpen(false);
    };

    const handleItemSelect = (selectedValue, setFieldValue, entries) => {
        if (selectedValue) {
            // Check if this item is already selected in other entries
            const isDuplicate = entries.some((entry, index) =>
                index !== currentItemIndex && entry.inventoryId === selectedValue
            );

            if (isDuplicate) {
                toast.warning("This item has already been added. Please select a different item.");
                return;
            }

            setFieldValue(`entries.${currentItemIndex}.inventoryId`, selectedValue);
        }
        setIsItemModalOpen(false);
    };

    // Memoized options
    const kitchenOptions = useMemo(() =>
        kitchenList.map(kitchen => ({
            label: kitchen.name,
            value: kitchen.id,
        })),
        [kitchenList]
    );

    return (
        <Container style={{ height }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Transfer Stock</h1>
                <button onClick={handleClose} type="button">
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={false}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100vh-120px)]">
                                <Wrapper>
                                    {/* Kitchen Selection */}
                                    <InputGroup>
                                        <Label htmlFor="kitchen">Select Kitchen *</Label>
                                        <FieldWrappers
                                            onClick={() => setIsKitchenModalOpen(true)}
                                            className="cursor-pointer"
                                        >
                                            <Field
                                                type="text"
                                                name="kitchenDisplay"
                                                id="kitchenDisplay"
                                                autoComplete="off"
                                                readOnly
                                                value={getKitchenName(values.assignedToId)}
                                                className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                placeholder="Select Kitchen"
                                            />
                                            <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                        </FieldWrappers>
                                        {errors.assignedToId && touched.assignedToId && (
                                            <ErrorText>{errors.assignedToId}</ErrorText>
                                        )}
                                    </InputGroup>

                                    {/* Items Section */}
                                    <InputGroup>
                                        <Label>Items to Transfer *</Label>

                                        <FieldArray name="entries">
                                            {({ push, remove }) => (
                                                <div className="space-y-4">
                                                    {values.entries.map((entry, index) => {
                                                        const itemDetails = getItemDetails(entry.inventoryId);
                                                        const error = errors.entries?.[index];
                                                        const itemOptions = getAvailableItemOptions(index, values.entries);

                                                        return (
                                                            <ItemCard key={index}>
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex-1 space-y-3">
                                                                        {/* Item Selection */}
                                                                        <InputGroup>
                                                                            <Label>Select Item *</Label>
                                                                            <FieldWrappers
                                                                                onClick={() => {
                                                                                    setCurrentItemIndex(index);
                                                                                    setIsItemModalOpen(true);
                                                                                }}
                                                                                className="cursor-pointer"
                                                                            >
                                                                                <Field
                                                                                    type="text"
                                                                                    name={`entries.${index}.itemDisplay`}
                                                                                    readOnly
                                                                                    value={getItemName(entry.inventoryId)}
                                                                                    className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                                                    placeholder="Select Item"
                                                                                />
                                                                                <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                                                            </FieldWrappers>
                                                                            {error?.inventoryId && (
                                                                                <ErrorText>{error.inventoryId}</ErrorText>
                                                                            )}
                                                                        </InputGroup>

                                                                        {/* Quantity Input */}
                                                                        <InputGroup>
                                                                            <Label htmlFor={`entries.${index}.quantity`}>
                                                                                Quantity *
                                                                            </Label>
                                                                            <FieldWrappers>
                                                                                <Field
                                                                                    type="number"
                                                                                    name={`entries.${index}.quantity`}
                                                                                    id={`entries.${index}.quantity`}
                                                                                    max={itemDetails?.currentCount || 1}
                                                                                    // min="0.01"
                                                                                    step="0.01"
                                                                                    className="w-full"
                                                                                    placeholder="Enter quantity"
                                                                                // onChange={(e) => {
                                                                                //     const val = parseFloat(e.target.value) || 0;
                                                                                //     setFieldValue(`entries.${index}.quantity`, val);
                                                                                // }}
                                                                                />
                                                                            </FieldWrappers>
                                                                            {error?.quantity && (
                                                                                <ErrorText>{error.quantity}</ErrorText>
                                                                            )}

                                                                        </InputGroup>
                                                                    </div>

                                                                    {/* Remove Button */}
                                                                    {values.entries.length > 1 && (
                                                                        <RemoveButton
                                                                            type="button"
                                                                            onClick={() => remove(index)}
                                                                            aria-label="Remove item"
                                                                        >
                                                                            ×
                                                                        </RemoveButton>
                                                                    )}
                                                                </div>
                                                            </ItemCard>
                                                        );
                                                    })}

                                                    {/* Add Item Button at the bottom */}
                                                    <AddItemButtonWrapper>
                                                        <AddItemButton
                                                            type="button"
                                                            onClick={() => {
                                                                push({ inventoryId: "", quantity: 1 });
                                                            }}
                                                        >
                                                            <MdAdd size={18} />
                                                            Add Another Item
                                                        </AddItemButton>
                                                    </AddItemButtonWrapper>
                                                </div>
                                            )}
                                        </FieldArray>
                                    </InputGroup>


                                </Wrapper>
                            </ScrollableContent>

                            <ActionSection>
                                <SideCancelBtn type="button" onClick={handleClose}>
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={isSubmitting || addTransferLoading}
                                >
                                    {isSubmitting || addTransferLoading ? "Submitting..." : "Submit"}
                                </SubmitBtn>
                            </ActionSection>

                            {/* Kitchen Selection Modal */}
                            <SelectOptionModal
                                isOpen={isKitchenModalOpen}
                                onClose={() => setIsKitchenModalOpen(false)}
                                title="Select Kitchen"
                                options={kitchenOptions}
                                selectedValue={values.assignedToId}
                                onSelect={(value) => handleKitchenSelect(value, setFieldValue)}
                                searchPlaceholder="Search kitchens..."
                            />

                            {/* Item Selection Modal */}
                            {isItemModalOpen && (
                                <SelectOptionModal
                                    isOpen={isItemModalOpen}
                                    onClose={() => setIsItemModalOpen(false)}
                                    title="Select Item"
                                    options={getAvailableItemOptions(currentItemIndex, values.entries)}
                                    selectedValue={values.entries[currentItemIndex]?.inventoryId}
                                    onSelect={(value) => handleItemSelect(value, setFieldValue, values.entries)}
                                    searchPlaceholder="Search items..."
                                />
                            )}
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5 mt-1`;
const AvailableStockText = tw.div`text-xs text-gray-500 mt-1 ml-1`;
const ItemCard = tw.div`bg-gray-50 p-4 rounded-lg border border-gray-200 relative`;
const AddItemButtonWrapper = tw.div`flex justify-center mt-2`;
const AddItemButton = tw.button`
    flex items-center gap-2 
    text-sm text-primary-color font-medium 
    hover:text-primary-color/80 
    px-4 py-2 
    border border-dashed border-primary-color/30 
    rounded-lg 
    hover:border-primary-color 
    hover:bg-primary-color/5 
    transition-all duration-200
`;
const RemoveButton = tw.button`
    text-gray-400 hover:text-red-500 
    text-xl ml-2 p-1 
    transition-colors duration-200
`;
const InfoBox = tw.div`
    mt-4 p-4 
    bg-blue-50 
    border border-blue-200 
    rounded-lg
`;
const InfoTitle = tw.h4`
    text-sm font-medium text-blue-800 
    mb-1
`;
const InfoText = tw.p`
    text-sm text-blue-600
`;

export default AddStockTransfer;